import React, { useEffect, useState, useCallback, useRef } from "react";
import "./behaviour.scss";
import { Spinner } from "@material-tailwind/react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { getApiResponse } from "../../APIs/getApiResponse";
import { endpoints } from "../../APIs/endpoints";
import { handleError } from "../../APIs/apiUtils";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BehaviourRightSec from "./Base"; 
import SingleDatePicker from "../../commonComp/SingleDatePicker";

const getOrdinalSuffix = (number) => {
  if (!number) return 'Class';
  const num = parseInt(number, 10);
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = num % 100;
  const suffix = suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0];
  return `Class ${num}<sup>${suffix}</sup>`;
};

const Index = () => {
  const location = useLocation();
  const classes = ["10"];
  const { date } = useParams();
  const navigate = useNavigate();

  const [selectedClassValue, setSelectedClassValue] = useState("10");
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0); 
  const [isLoading, setIsLoading] = useState(true);
  const [calendarDates, setCalendarDates] = useState(new Set());
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);

  const classDropdownRef = useRef(null);

  useEffect(() => {
    const storedDatesJSON = localStorage.getItem('calendarDates');
    if (storedDatesJSON) {
      try {
        const storedDatesArray = JSON.parse(storedDatesJSON);
        setCalendarDates(new Set(storedDatesArray));
      } catch (err) {
        console.error("Failed to parse calendar dates from localStorage:", err);
      }
    }
  }, []);

  useEffect(() => {
    let newDateRange;
    if (location.state?.fromDate && location.state?.toDate) {
      newDateRange = { startDate: location.state.fromDate, endDate: location.state.toDate };
      navigate(location.pathname, { replace: true, state: {} });
    } else if (date) {
      const [day, month, year] = date.split("-");
      const formatted = `${day}/${month}/${year}`;
      newDateRange = { startDate: formatted, endDate: formatted };
    } else {
      newDateRange = { startDate: null, endDate: null };
      setIsLoading(false);
    }
    setDateRange(newDateRange);
  }, [date, location.pathname, location.state, navigate]);

  const fetchBehaviourData = useCallback(async (currentDateRange, currentClass) => {
    if (!currentDateRange.startDate || !currentClass) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setDashboardData(null);
    setActiveTabIndex(0); 
    try {
      const urlParams = new URLSearchParams({
        class: currentClass,
        fromDate: currentDateRange.startDate,
        toDate: currentDateRange.endDate,
      });
      const response = await getApiResponse(`${endpoints.BEHAVIOURAL}?${urlParams}`);
      const newData = response?.data || {};
      setDashboardData(newData);
      
      const periods = newData?.data;
      if (Array.isArray(periods) && periods.length > 0) {
        setActiveTabIndex(0);
      }
      
    } catch (error) {
      handleError(error);
      setDashboardData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dateRange.startDate && selectedClassValue) {
      fetchBehaviourData(dateRange, selectedClassValue);
    } else if (!dateRange.startDate) {
      setIsLoading(false);
    }
  }, [dateRange, selectedClassValue, fetchBehaviourData]);

  const handleClassChange = (val) => {
    setSelectedClassValue(val);
    setIsClassDropdownOpen(false);
  };


  const handleDateChange = (range) => {
    setDateRange(range);
    if (range.startDate) {
      const formattedUrlDate = range.startDate.replace(/\//g, '-');
      navigate(`/dashboard/${formattedUrlDate}`);
    } else {
      navigate(`/`);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (classDropdownRef.current && !classDropdownRef.current.contains(event.target)) {
        setIsClassDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="behaviour-container p-2 sm:p-4 md:p-6 font-Archivo ">
      <ToastContainer style={{zIndex: 80}} />
      <div className="behaviour-left-section font-Archivo ">
        <div className="date-class-container flex flex-row flex-wrap items-center gap-2 mb-3 w-full font-Archivo ml-3">
          <div>
            <SingleDatePicker 
              onDateSelect={handleDateChange}
              initialSelectedDate={date}
            />
          </div>
          <div className="relative w-full  sm:w-[180px] font-Archivo" ref={classDropdownRef}>
            <button
              onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
              className="flex items-center self-start sm:self-center mt-2 sm:mt-0 px-3 py-2 text-sm text-gray-600 bg-white border rounded-lg w-32 font-[Archivo]"
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: getOrdinalSuffix(selectedClassValue),
                }}
              />
              <svg
                className="w-7 h-4 text-#1F2937"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 26  26"
              >
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isClassDropdownOpen && (
              <div
                className="absolute z-10 left-0 mt-2 w-32 bg-white border hover:border hover:border-[#2563EB] font-[Archivo] font:weight-[600]  font:family-[SemiBold] text-[16px] text-#1F2937 rounded-lg shadow-sm max-h-[200px] overflow-y-auto"
                style={{ scrollbarWidth: "thin", scrollbarColor: "#6B7280 #F8F8F8" }}
              >
                {classes.map((cls) => (
                  <button
                    key={cls}
                    onClick={() => handleClassChange(cls)}
                    className="w-full pl-4 pr-2 py-2 text-left hover:bg-gray-100 font-Archivo rounded text-[14px]"
                    >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: getOrdinalSuffix(cls),
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {isLoading || !dateRange.startDate ? (
          <div className="flex justify-center items-center h-[80vh]">
            <Spinner className="h-10 w-10 md:h-12 md:w-12" color="blue" />
          </div>
        ) : (
          <div>
            {/* MODIFICATION: Check if the `data` property is an array with items. */}
            {dashboardData?.data && Array.isArray(dashboardData.data) && dashboardData.data.length > 0 ? (
              <BehaviourRightSec
                data={dashboardData.data}
                metadata={dashboardData.meta}
                // MODIFICATION: Pass the active tab index and the state setter function as props.
                activeTabIndex={activeTabIndex}
                onTabChange={setActiveTabIndex}
                fromDate={dateRange.startDate}
                toDate={dateRange.endDate}
                classInfo={selectedClassValue}
              />
            ) : (
              <div className="flex flex-col justify-center items-center h-[80vh] bg-gray-100 w-full mt-4 my-auto p-4 rounded-lg">
                <img src="/images/Layer 1.png" alt="No data" className="w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 mb-4 sm:mb-8" />
                <p className="text-gray-600 text-center text-sm sm:text-base md:text-lg font-Archivo">
                  No data available on this date
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Index;